import React, { useState, useEffect, useRef } from 'react';
import api from '../../api';

const WEATHER_EMOJI = {
  Clear: '☀️', 'Mainly clear': '🌤️', 'Partly cloudy': '⛅', Overcast: '☁️',
  Fog: '🌫️', Rain: '🌧️', 'Light rain': '🌦️', 'Heavy rain': '⛈️',
  Drizzle: '🌦️', 'Light drizzle': '🌦️', Snow: '🌨️', Thunderstorm: '⛈️', Showers: '🌧️',
};
function weatherEmoji(summary) { return WEATHER_EMOJI[summary] || '🌡️'; }

function StatCard({ label, value, sub, color = '#0d9488', tooltip }) {
  const [show, setShow] = React.useState(false);
  return (
    <div className="card" style={{ padding: '18px 20px', flex: 1, position: 'relative' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
        {label}
        {tooltip && (
          <span
            onMouseEnter={() => setShow(true)}
            onMouseLeave={() => setShow(false)}
            style={{ cursor: 'help', fontSize: 11, color: '#94a3b8', lineHeight: 1 }}
          >ⓘ</span>
        )}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{sub}</div>}
      {tooltip && show && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 50, marginTop: 6,
          background: 'var(--card-bg, #fff)', border: '1px solid rgba(0,0,0,0.1)',
          borderRadius: 8, padding: '12px 14px', width: 230,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', fontSize: 12, lineHeight: 1.7,
        }}>
          {tooltip}
        </div>
      )}
    </div>
  );
}

function DropZone({ onFile, busy }) {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef();

  function handleDrop(e) {
    e.preventDefault();
    setDrag(false);
    const file = e.dataTransfer.files[0];
    if (file) onFile(file);
  }

  return (
    <div
      onClick={() => !busy && inputRef.current.click()}
      onDragOver={e => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={handleDrop}
      style={{
        border: `2px dashed ${drag ? '#0d9488' : 'rgba(13,148,136,0.3)'}`,
        borderRadius: 12, padding: '48px 24px', textAlign: 'center',
        cursor: busy ? 'default' : 'pointer',
        background: drag ? 'rgba(13,148,136,0.06)' : 'transparent',
        transition: 'all .2s',
      }}
    >
      <input ref={inputRef} type="file" accept=".csv" style={{ display: 'none' }}
        onChange={e => e.target.files[0] && onFile(e.target.files[0])} />
      <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>
        {busy ? 'Importing…' : 'Drop your Toast CSV here'}
      </div>
      <div style={{ fontSize: 13, color: '#64748b' }}>
        Or click to browse · Orders/Checks or Daypart Summary exports accepted
      </div>
    </div>
  );
}

function InsightRow({ label, value, sub }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid rgba(255,255,255,.05)' }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: '#64748b' }}>{sub}</div>}
      </div>
      <div style={{ fontWeight: 700, fontSize: 15, color: '#0d9488' }}>{value}</div>
    </div>
  );
}

// ── Meal period bar chart ────────────────────────────────────────────────────
const PERIOD_ORDER = ['Breakfast', 'Lunch', 'Afternoon', 'Dinner', 'Late Night'];
const PERIOD_COLOR = {
  Breakfast: '#f59e0b', Lunch: '#10b981',
  Afternoon: '#3b82f6', Dinner: '#8b5cf6', 'Late Night': '#ec4899',
};
const PERIOD_TIMES = {
  Breakfast: 'before 11am',
  Lunch: '11am – 3pm',
  Afternoon: '3pm – 5pm',
  Dinner: '5pm – 10pm',
  'Late Night': '10pm+',
};

function MealPeriodChart({ data, overall }) {
  const [open, setOpen] = useState(false);
  const sorted = [...data].sort((a, b) =>
    PERIOD_ORDER.indexOf(a.meal_period) - PERIOD_ORDER.indexOf(b.meal_period)
  );
  const maxCovers = Math.max(...sorted.map(r => r.avg_covers), 1);

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div
        className="card-header"
        onClick={() => setOpen(o => !o)}
        style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <span>⏰ Busiest Times of Day</span>
        <span style={{ fontSize: 12, opacity: 0.5 }}>{open ? '▲ collapse' : '▼ expand'}</span>
      </div>
      {open && (
        <div className="card-body">
          <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 14 }}>
            Average covers by meal period across your full history
          </div>
          {sorted.map(r => {
            const pct    = Math.round((r.avg_covers / maxCovers) * 100);
            const color  = PERIOD_COLOR[r.meal_period] || '#0d9488';
            const vsAvg  = overall > 0 ? Math.round((r.avg_covers / overall) * 100) : null;
            return (
              <div key={r.meal_period} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{r.meal_period}</span>
                    {PERIOD_TIMES[r.meal_period] && (
                      <span style={{ fontSize: 11, opacity: 0.5, marginLeft: 6 }}>{PERIOD_TIMES[r.meal_period]}</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, opacity: 0.55 }}>{r.days} days</span>
                    {vsAvg !== null && (
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                        background: vsAvg >= 100 ? 'rgba(22,163,74,.12)' : 'rgba(100,116,139,.1)',
                        color: vsAvg >= 100 ? '#16a34a' : undefined,
                      }}>
                        {vsAvg}% of daily
                      </span>
                    )}
                    <span style={{ fontWeight: 700, color, fontSize: 14 }}>{r.avg_covers} avg</span>
                  </div>
                </div>
                <div style={{ height: 8, background: 'rgba(128,128,128,0.12)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', width: `${pct}%`, background: color,
                    borderRadius: 4, transition: 'width .4s ease',
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── DOW line chart: Lunch + Dinner covers across Mon–Sun ────────────────────
const DOW_ORDER = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

function DowLineChart({ byDowMealPeriod = [] }) {
  if (!byDowMealPeriod.length) return null;

  // Build lookup: { Monday: { Lunch: 120, Dinner: 180 }, … }
  const map = {};
  byDowMealPeriod.forEach(r => {
    if (!map[r.day_of_week]) map[r.day_of_week] = {};
    map[r.day_of_week][r.meal_period] = parseInt(r.avg_covers) || 0;
  });

  const hasLunch  = byDowMealPeriod.some(r => r.meal_period === 'Lunch');
  const hasDinner = byDowMealPeriod.some(r => r.meal_period === 'Dinner');
  if (!hasLunch && !hasDinner) return null;

  const lunchVals  = DOW_ORDER.map(d => map[d]?.Lunch  ?? null);
  const dinnerVals = DOW_ORDER.map(d => map[d]?.Dinner ?? null);
  const allVals    = [...lunchVals, ...dinnerVals].filter(v => v != null);
  const maxVal     = Math.max(...allVals, 1);

  const W = 440, H = 110, padL = 28, padR = 10, padT = 10, padB = 24;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const xStep  = innerW / (DOW_ORDER.length - 1);

  function toXY(vals) {
    return vals.map((v, i) => ({
      x: padL + i * xStep,
      y: v == null ? null : padT + innerH - (v / maxVal) * innerH,
      v,
    }));
  }

  function buildPath(pts) {
    const valid = pts.filter(p => p.y != null);
    if (valid.length < 2) return '';
    // Smooth curve using cubic bezier control points
    return valid.map((p, i) => {
      if (i === 0) return `M${p.x.toFixed(1)},${p.y.toFixed(1)}`;
      const prev = valid[i - 1];
      const cx1 = (prev.x + p.x) / 2;
      const cy1 = prev.y;
      const cx2 = (prev.x + p.x) / 2;
      const cy2 = p.y;
      return `C${cx1.toFixed(1)},${cy1.toFixed(1)} ${cx2.toFixed(1)},${cy2.toFixed(1)} ${p.x.toFixed(1)},${p.y.toFixed(1)}`;
    }).join(' ');
  }

  function buildArea(pts, baseline) {
    const valid = pts.filter(p => p.y != null);
    if (valid.length < 2) return '';
    const linePath = buildPath(pts);
    return `${linePath} L${valid[valid.length-1].x.toFixed(1)},${baseline} L${valid[0].x.toFixed(1)},${baseline} Z`;
  }

  const lunchPts  = toXY(lunchVals);
  const dinnerPts = toXY(dinnerVals);
  const baseline  = padT + innerH;

  // Y-axis ticks
  const yTicks = [0, Math.round(maxVal / 2), maxVal];

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 14 }}>
        Weekly pattern
        {hasLunch  && <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 500, textTransform: 'none', letterSpacing: 0, fontSize: 11 }}><span style={{ width: 18, height: 2, background: '#10b981', display: 'inline-block', borderRadius: 1 }} />Lunch</span>}
        {hasDinner && <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 500, textTransform: 'none', letterSpacing: 0, fontSize: 11 }}><span style={{ width: 18, height: 2, background: '#8b5cf6', display: 'inline-block', borderRadius: 1 }} />Dinner</span>}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}>
        <defs>
          <linearGradient id="lunchGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.18"/>
            <stop offset="100%" stopColor="#10b981" stopOpacity="0"/>
          </linearGradient>
          <linearGradient id="dinnerGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.18"/>
            <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0"/>
          </linearGradient>
        </defs>

        {/* Y-axis grid lines */}
        {yTicks.map(tick => {
          const y = padT + innerH - (tick / maxVal) * innerH;
          return (
            <g key={tick}>
              <line x1={padL} x2={W - padR} y1={y} y2={y} stroke="currentColor" strokeOpacity="0.07" strokeWidth="1"/>
              <text x={padL - 4} y={y + 3.5} textAnchor="end" fontSize="8" fill="currentColor" fillOpacity="0.4">{tick}</text>
            </g>
          );
        })}

        {/* Area fills */}
        {hasLunch  && <path d={buildArea(lunchPts,  baseline)} fill="url(#lunchGrad)" />}
        {hasDinner && <path d={buildArea(dinnerPts, baseline)} fill="url(#dinnerGrad)" />}

        {/* Lines */}
        {hasLunch  && <path d={buildPath(lunchPts)}  fill="none" stroke="#10b981" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"/>}
        {hasDinner && <path d={buildPath(dinnerPts)} fill="none" stroke="#8b5cf6" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"/>}

        {/* Dots + value labels */}
        {hasLunch && lunchPts.map((p, i) => p.y != null && (
          <g key={`l${i}`}>
            <circle cx={p.x} cy={p.y} r="3" fill="#10b981"/>
            <text x={p.x} y={p.y - 6} textAnchor="middle" fontSize="8.5" fill="#10b981" fontWeight="700">{p.v}</text>
          </g>
        ))}
        {hasDinner && dinnerPts.map((p, i) => p.y != null && (
          <g key={`d${i}`}>
            <circle cx={p.x} cy={p.y} r="3" fill="#8b5cf6"/>
            <text x={p.x} y={p.y - 6} textAnchor="middle" fontSize="8.5" fill="#8b5cf6" fontWeight="700">{p.v}</text>
          </g>
        ))}

        {/* X-axis day labels */}
        {DOW_ORDER.map((day, i) => (
          <text key={day} x={padL + i * xStep} y={H - 6} textAnchor="middle" fontSize="9" fill="currentColor" fillOpacity="0.5">
            {day.slice(0, 3)}
          </text>
        ))}
      </svg>
    </div>
  );
}

// ── Computed insight bullets from stats ──────────────────────────────────────
function Insights({ stats }) {
  if (!stats?.byDow?.length) return null;

  const byDow     = stats.byDow || [];
  const byWeather = stats.byWeather || [];

  const sorted     = [...byDow].sort((a, b) => b.avg_covers - a.avg_covers);
  const best       = sorted[0];
  const worst      = sorted[sorted.length - 1];

  const weekend    = byDow.filter(d => ['Saturday','Sunday'].includes(d.day_of_week));
  const weekday    = byDow.filter(d => !['Saturday','Sunday'].includes(d.day_of_week));
  const wkendAvg   = weekend.length ? Math.round(weekend.reduce((s,d) => s + +d.avg_covers, 0) / weekend.length) : null;
  const wkdayAvg   = weekday.length ? Math.round(weekday.reduce((s,d) => s + +d.avg_covers, 0) / weekday.length) : null;
  const wkPct      = wkendAvg && wkdayAvg ? Math.round((wkendAvg - wkdayAvg) / wkdayAvg * 100) : null;

  const rainyRow   = byWeather.find(w => ['Rain','Light rain','Heavy rain','Drizzle','Light drizzle','Showers','Heavy drizzle'].includes(w.weather_summary));
  const clearRow   = byWeather.find(w => ['Clear','Mainly clear'].includes(w.weather_summary));
  const rainImpact = rainyRow && clearRow ? Math.round((rainyRow.avg_covers - clearRow.avg_covers) / clearRow.avg_covers * 100) : null;

  const bullets = [
    best  && { icon: '🏆', text: `${best.day_of_week} is your busiest day — ${best.avg_covers} avg covers` },
    worst && best.day_of_week !== worst.day_of_week && { icon: '📉', text: `${worst.day_of_week} is your slowest — ${worst.avg_covers} avg covers` },
    wkPct !== null && { icon: wkPct >= 0 ? '📈' : '📉', text: `Weekends run ${Math.abs(wkPct)}% ${wkPct >= 0 ? 'busier' : 'slower'} than weekdays (${wkendAvg} vs ${wkdayAvg} avg)` },
    rainImpact !== null && { icon: rainImpact < 0 ? '🌧️' : '☔', text: `Rain ${rainImpact < 0 ? 'drops' : 'adds'} covers by ${Math.abs(rainImpact)}% vs clear days (${rainyRow.avg_covers} vs ${clearRow.avg_covers} avg)` },
  ].filter(Boolean);

  if (!bullets.length) return null;

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">✦ Insights</div>
      <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {bullets.map((b, i) => (
          <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 22, flexShrink: 0, lineHeight: 1.2 }}>{b.icon}</span>
            <span style={{ fontSize: 14, lineHeight: 1.55 }}>{b.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── AI chat ──────────────────────────────────────────────────────────────────
const SUGGESTIONS = [
  "What's our busiest day?",
  "How does rain affect us?",
  "What should I staff on Fridays?",
  "Any seasonal patterns?",
];

function CoversChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState('');
  const [busy, setBusy]         = useState(false);
  const bottomRef               = useRef();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function ask(question) {
    const q = question || input.trim();
    if (!q || busy) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: q }]);
    setBusy(true);
    try {
      const r = await api.post('/covers/ask', { question: q });
      setMessages(prev => [...prev, { role: 'ai', text: r.data.answer }]);
    } catch {
      setMessages(prev => [...prev, { role: 'ai', text: "Sorry, I couldn't answer that right now." }]);
    } finally { setBusy(false); }
  }

  return (
    <div className="card">
      <div className="card-header">💬 Ask about your data</div>
      <div className="card-body">
        {/* Suggestion chips — only before first message */}
        {messages.length === 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {SUGGESTIONS.map(s => (
              <button key={s} onClick={() => ask(s)} className="btn btn-ghost btn-sm" style={{ fontSize: 12 }}>{s}</button>
            ))}
          </div>
        )}

        {/* Message thread */}
        {messages.length > 0 && (
          <div style={{ maxHeight: 320, overflowY: 'auto', marginBottom: 12 }}>
            {messages.map((m, i) => (
              <div key={i} style={{
                marginBottom: 14,
                display: 'flex',
                flexDirection: 'column',
                alignItems: m.role === 'user' ? 'flex-end' : 'flex-start',
              }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, marginBottom: 4,
                  color: m.role === 'user' ? '#0d9488' : '#818cf8',
                }}>
                  {m.role === 'user' ? 'You' : '✦ Covers AI'}
                </div>
                <div style={{
                  maxWidth: '85%', fontSize: 13, lineHeight: 1.6,
                  padding: '10px 14px', borderRadius: 10,
                  background: m.role === 'user'
                    ? 'rgba(13,148,136,0.12)'
                    : 'rgba(129,140,248,0.08)',
                  border: `1px solid ${m.role === 'user' ? 'rgba(13,148,136,0.25)' : 'rgba(129,140,248,0.2)'}`,
                  whiteSpace: 'pre-wrap',
                }}>
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
                <span style={{ animation: 'pulse 1s infinite' }}>Thinking…</span>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        )}

        {/* Input */}
        <form onSubmit={e => { e.preventDefault(); ask(); }} style={{ display: 'flex', gap: 8 }}>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Ask anything about your covers data…"
            style={{ flex: 1, fontSize: 13 }}
            disabled={busy}
          />
          <button className="btn btn-primary btn-sm" type="submit" disabled={busy || !input.trim()}>
            Ask
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Server leaderboard ───────────────────────────────────────────────────────
const MEDALS = ['🥇', '🥈', '🥉'];
const PERIOD_COLOR_SM = {
  Breakfast: '#f59e0b', Lunch: '#10b981',
  Afternoon: '#3b82f6', Dinner: '#8b5cf6', 'Late Night': '#ec4899',
};

// Sort helper
function sortRows(list, key, dir) {
  return [...list].sort((a, b) => {
    const av = parseFloat(a[key]) || 0;
    const bv = parseFloat(b[key]) || 0;
    return dir === 'asc' ? av - bv : bv - av;
  });
}

// Clickable sort column header
function SortCol({ label, note, sortKey, active, dir, onSort }) {
  return (
    <div onClick={() => onSort(sortKey)} style={{ textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}>
      <div style={{
        fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em',
        color: active ? '#0d9488' : '#64748b', display: 'inline-flex', alignItems: 'center', gap: 3,
      }}>
        {label}{active && <span>{dir === 'asc' ? ' ↑' : ' ↓'}</span>}
      </div>
      {note && <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 1 }}>{note}</div>}
    </div>
  );
}

const MIN_CHECKS = 10; // fewer than this = too small to rank fairly

// One row in a service-specific table (Lunch tab or Dinner tab)
function ServiceRow({ mp, rank, isBanquet, maxChk }) {
  const checks     = parseInt(mp.total_checks) || 0;
  const thinSample = checks < MIN_CHECKS;
  const barW       = maxChk > 0 ? Math.round((+mp.avg_sales_per_check / maxChk) * 100) : 0;
  const color      = thinSample ? '#94a3b8' : (PERIOD_COLOR_SM[mp.meal_period] || '#0d9488');
  const revPerShift = mp.shifts_worked > 0
    ? (parseFloat(mp.total_sales) / parseInt(mp.shifts_worked)).toFixed(0)
    : null;

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '28px 1fr 82px 72px 82px 62px',
      gap: 8, alignItems: 'center', padding: '10px 8px', borderRadius: 8,
      opacity: thinSample ? 0.45 : 1,
    }}>
      {/* Rank / medal */}
      <div style={{ fontSize: 18, textAlign: 'center' }}>
        {isBanquet  ? '🎪'
          : thinSample ? <span style={{ fontSize: 11 }} title={`Only ${checks} checks — too few to rank`}>⚠️</span>
          : MEDALS[rank] || <span style={{ fontSize: 12, color: '#64748b' }}>{rank + 1}</span>}
      </div>

      {/* Name + subline + bar */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>{mp.server_name}</span>
          {thinSample && (
            <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, fontWeight: 700,
              background: 'rgba(148,163,184,0.15)', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.04em' }}>
              n={checks}
            </span>
          )}
        </div>
        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>
          {mp.total_checks} chks · {mp.total_covers} covers · {mp.shifts_worked} shifts
        </div>
        <div style={{ height: 3, background: 'rgba(128,128,128,0.12)', borderRadius: 2, marginTop: 4, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${barW}%`, background: rank < 3 && !isBanquet && !thinSample ? color : '#64748b', borderRadius: 2 }} />
        </div>
      </div>

      {/* $/check */}
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 14, fontWeight: 800, color }}>${Number(mp.avg_sales_per_check).toFixed(2)}</div>
        <div style={{ fontSize: 10, color: '#94a3b8' }}>$/check</div>
      </div>

      {/* chks/shift */}
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>{Number(mp.checks_per_shift).toFixed(1)}</div>
        <div style={{ fontSize: 10, color: '#94a3b8' }}>chks/shift</div>
      </div>

      {/* $/shift */}
      <div style={{ textAlign: 'right' }}>
        {revPerShift != null
          ? <>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#0d9488' }}>
                ${Number(revPerShift).toLocaleString('en-US', { maximumFractionDigits: 0 })}
              </div>
              <div style={{ fontSize: 10, color: '#94a3b8' }}>$/shift</div>
            </>
          : <div style={{ fontSize: 12, color: '#94a3b8' }}>—</div>}
      </div>

      {/* Discount % */}
      <div style={{ textAlign: 'right' }}>
        {mp.discount_pct != null
          ? <>
              <div style={{ fontSize: 14, fontWeight: 800, color: parseFloat(mp.discount_pct) >= 5 ? '#ef4444' : parseFloat(mp.discount_pct) >= 2 ? '#d97706' : '#64748b' }}>
                {Number(mp.discount_pct).toFixed(1)}%
              </div>
              <div style={{ fontSize: 10, color: '#94a3b8' }}>disc%</div>
            </>
          : <div style={{ fontSize: 12, color: '#94a3b8' }}>—</div>}
      </div>

    </div>
  );
}

function ServiceTable({ rows, isBanquet, sortKey, sortDir, onSort }) {
  const sorted  = sortRows(rows, sortKey, sortDir);
  const maxChk  = Math.max(...sorted.filter(r => parseInt(r.total_checks) >= MIN_CHECKS).map(r => +r.avg_sales_per_check || 0), 1);
  const GRID    = '28px 1fr 82px 72px 82px 62px';

  // Assign rank sequentially in sorted order, counting only reliable servers
  let reliableCounter = 0;
  const ranked = sorted.map(mp => ({
    ...mp,
    displayRank: parseInt(mp.total_checks) >= MIN_CHECKS ? reliableCounter++ : 99,
  }));

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 8, padding: '4px 8px 8px', borderBottom: '1px solid rgba(128,128,128,0.1)', marginBottom: 4 }}>
        <div />
        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#64748b' }}>Server</div>
        <SortCol label="$/check"    sortKey="avg_sales_per_check" active={sortKey==='avg_sales_per_check'} dir={sortDir} onSort={onSort} />
        <SortCol label="Chks/shift" sortKey="checks_per_shift"    active={sortKey==='checks_per_shift'}   dir={sortDir} onSort={onSort} />
        <SortCol label="$/shift"    sortKey="rev_per_shift"       active={sortKey==='rev_per_shift'}      dir={sortDir} onSort={onSort} />
        <SortCol label="Disc%"      sortKey="discount_pct"        active={sortKey==='discount_pct'}       dir={sortDir} onSort={onSort} />
      </div>
      {ranked.map(mp => (
        <ServiceRow key={mp.server_name} mp={mp} rank={mp.displayRank} isBanquet={isBanquet} maxChk={maxChk} />
      ))}
    </div>
  );
}

// Auto-generated insight chips shown above the table
function ServerInsights({ rows, tab }) {
  const reliable = rows.filter(r => parseInt(r.total_checks) >= MIN_CHECKS);
  if (!reliable.length) return null;

  const insights = [];

  // Top $/check
  const topChk = [...reliable].sort((a,b) => +b.avg_sales_per_check - +a.avg_sales_per_check)[0];
  if (topChk) {
    const label = tab === 'All' ? 'overall' : tab.toLowerCase();
    insights.push({
      icon: '🏆',
      text: `${topChk.server_name} leads ${label} at $${Number(topChk.avg_sales_per_check).toFixed(0)}/check`,
      color: '#0d9488',
    });
  }

  // Top $/shift (revenue generator)
  const withRev = reliable.map(r => ({
    ...r,
    rev_per_shift: parseInt(r.shifts_worked) > 0 ? parseFloat(r.total_sales) / parseInt(r.shifts_worked) : 0,
  }));
  const topShift = [...withRev].sort((a,b) => b.rev_per_shift - a.rev_per_shift)[0];
  if (topShift && topShift.server_name !== topChk?.server_name) {
    insights.push({
      icon: '💰',
      text: `${topShift.server_name} drives the most revenue per shift — $${Math.round(topShift.rev_per_shift).toLocaleString('en-US')}/shift`,
      color: '#16a34a',
    });
  }

  // Most active (highest chks/shift among reliable)
  const topBusy = [...reliable].sort((a,b) => +b.checks_per_shift - +a.checks_per_shift)[0];
  if (topBusy && topBusy.server_name !== topChk?.server_name && topBusy.server_name !== topShift?.server_name) {
    insights.push({
      icon: '⚡',
      text: `${topBusy.server_name} runs the most tables — ${Number(topBusy.checks_per_shift).toFixed(1)} chks/shift`,
      color: '#d97706',
    });
  }

  // Highest discount % — flag the outlier
  const withDisc   = reliable.filter(r => r.discount_pct != null && +r.discount_pct > 0);
  const topDisc    = [...withDisc].sort((a,b) => +b.discount_pct - +a.discount_pct)[0];
  const avgDiscPct = withDisc.length ? withDisc.reduce((s,r) => s + +r.discount_pct, 0) / withDisc.length : 0;
  if (topDisc && +topDisc.discount_pct > avgDiscPct * 1.5) {
    insights.push({
      icon: '🎟️',
      text: `${topDisc.server_name} discounts ${Number(topDisc.discount_pct).toFixed(1)}% of gross — team avg is ${avgDiscPct.toFixed(1)}%`,
      color: '#ef4444',
    });
  }

  // Small sample warning
  const thinCount = rows.length - reliable.length;
  if (thinCount > 0) {
    insights.push({
      icon: '⚠️',
      text: `${thinCount} server${thinCount > 1 ? 's' : ''} dimmed — fewer than ${MIN_CHECKS} checks, not enough to rank fairly`,
      color: '#94a3b8',
    });
  }

  if (!insights.length) return null;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
      {insights.map((ins, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 11px', borderRadius: 20,
          background: ins.color + '12', border: `1px solid ${ins.color}33`,
          fontSize: 12, color: ins.color, fontWeight: 500,
        }}>
          <span>{ins.icon}</span>
          <span>{ins.text}</span>
        </div>
      ))}
    </div>
  );
}

function ServerLeaderboard({ expanded: defaultOpen = false }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [apiError, setApiError] = useState(null);
  const [period, setPeriod]     = useState(90);
  const [open, setOpen]         = useState(defaultOpen);
  const [serviceTab, setServiceTab] = useState('Lunch'); // 'Lunch' | 'Dinner' | 'All'
  const [sortKey, setSortKey]   = useState('rev_per_shift');
  const [sortDir, setSortDir]   = useState('desc');

  useEffect(() => {
    setLoading(true);
    setApiError(null);
    api.get(`/covers/server-stats?days=${period}`)
      .then(r => setData(r.data))
      .catch(e => { setApiError(e.response?.data?.error || `Error ${e.response?.status}`); setData(null); })
      .finally(() => setLoading(false));
  }, [period]);

  function handleSort(key) {
    if (key === sortKey) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  }

  const { leaderboard: rawBoard = [], banquet: rawBanquet = [], byMealPeriod = [], dateRange } = data || {};
  if (!loading && !rawBoard.length && !defaultOpen) return null;

  // Classify a row by its dining_area value (from Toast export) or name fallback
  const isBarRow     = r => /bar/i.test(r.dining_area || '');
  const isBanquetRow = r => !isBarRow(r) && /banquet|event|catering|private\s*dining|group/i.test(r.dining_area || r.server_name || '');
  const isFloorRow   = r => !isBarRow(r) && !isBanquetRow(r);

  // Build service-specific rows from byMealPeriod, adding rev_per_shift
  function enrichRow(r) {
    const shifts   = parseInt(r.shifts_worked)  || 0;
    const sales    = parseFloat(r.total_sales)  || 0;
    const discPct  = parseFloat(r.discount_pct) || 0;
    const revShift = shifts > 0 ? +(sales / shifts).toFixed(2) : 0;
    const score    = +(revShift * (1 - discPct / 100)).toFixed(2);
    return { ...r, rev_per_shift: revShift, score };
  }
  function getServiceRows(mealPeriod) {
    return byMealPeriod.filter(r => r.meal_period === mealPeriod && isFloorRow(r)).map(enrichRow);
  }
  function getBanquetRows(mealPeriod) {
    return byMealPeriod.filter(r => r.meal_period === mealPeriod && isBanquetRow(r)).map(enrichRow);
  }
  function getBarRows(mealPeriod) {
    return byMealPeriod.filter(r => r.meal_period === mealPeriod && isBarRow(r)).map(enrichRow);
  }

  // For "All" tab — aggregate across all meal periods per server
  const allServersMap = {};
  byMealPeriod.forEach(r => {
    if (!allServersMap[r.server_name]) {
      allServersMap[r.server_name] = {
        server_name: r.server_name, dining_area: r.dining_area,
        total_checks: 0, total_covers: 0, total_sales: 0, total_with_tip: 0, shifts_worked: 0,
      };
    }
    const s = allServersMap[r.server_name];
    s.total_checks      += parseInt(r.total_checks)        || 0;
    s.total_covers      += parseInt(r.total_covers)        || 0;
    s.total_sales       += parseFloat(r.total_sales)       || 0;
    s.total_with_tip    += parseFloat(r.total_with_tip || r.total_sales) || 0;
    s.discount_amount   += parseFloat(r.discount_amount)   || 0;
    s.shifts_worked     += parseInt(r.shifts_worked)       || 0;
    if (r.dining_area) s.dining_area = r.dining_area;
  });
  Object.values(allServersMap).forEach(s => {
    s.avg_sales_per_check = s.total_checks  > 0 ? +(s.total_sales / s.total_checks).toFixed(2)  : 0;
    s.avg_sales_per_cover = s.total_covers  > 0 ? +(s.total_sales / s.total_covers).toFixed(2)  : 0;
    s.checks_per_shift    = s.shifts_worked > 0 ? +(s.total_checks / s.shifts_worked).toFixed(1) : 0;
    s.rev_per_shift       = s.shifts_worked > 0 ? +(s.total_sales  / s.shifts_worked).toFixed(2) : 0;
    const gross = s.total_sales + s.discount_amount;
    s.discount_pct = gross > 0 && s.discount_amount > 0
      ? +(s.discount_amount / gross * 100).toFixed(1) : null;
    const discPct = parseFloat(s.discount_pct) || 0;
    s.score = +(s.rev_per_shift * (1 - discPct / 100)).toFixed(2);
    s.meal_period = 'All';
  });
  const allRows    = Object.values(allServersMap).filter(isFloorRow);
  const allBanquet = Object.values(allServersMap).filter(isBanquetRow);
  const allBar     = Object.values(allServersMap).filter(isBarRow);

  const tabColor = serviceTab === 'Lunch' ? PERIOD_COLOR_SM['Lunch']
    : serviceTab === 'Dinner' ? PERIOD_COLOR_SM['Dinner'] : '#0d9488';

  const currentRows    = serviceTab === 'All' ? allRows    : getServiceRows(serviceTab);
  const currentBanquet = serviceTab === 'All' ? allBanquet : getBanquetRows(serviceTab);
  const currentBar     = serviceTab === 'All' ? allBar     : getBarRows(serviceTab);

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      {!defaultOpen && (
        <div className="card-header" onClick={() => setOpen(o => !o)}
          style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>👥 Server Performance</span>
          <span style={{ fontSize: 12, opacity: 0.5 }}>{open ? '▲ collapse' : '▼ expand'}</span>
        </div>
      )}
      {defaultOpen && (
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>👥 Server Performance</span>
          {dateRange?.earliest && <span style={{ fontSize: 11, opacity: 0.45 }}>data from {dateRange.earliest}</span>}
        </div>
      )}

      {open && !loading && apiError && (
        <div className="card-body" style={{ color: '#ef4444', padding: '24px 20px' }}>⚠️ {apiError}</div>
      )}
      {open && loading && <div className="card-body" style={{ color: '#64748b', fontSize: 13 }}>Loading…</div>}
      {open && !loading && !apiError && !rawBoard.length && (
        <div className="card-body" style={{ textAlign: 'center', padding: '32px 20px', color: '#64748b' }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>📊</div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>No server data yet</div>
          <div style={{ fontSize: 13 }}>Import a Toast Orders CSV that includes a <strong>Server</strong> column.</div>
        </div>
      )}

      {open && !loading && !apiError && rawBoard.length > 0 && (
        <div className="card-body">

          {/* Controls row: period picker + service tabs */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <span style={{ fontSize: 11, opacity: 0.5, marginRight: 2 }}>Last:</span>
              {[30, 60, 90, 365].map(d => (
                <button key={d} onClick={e => { e.stopPropagation(); setPeriod(d); }}
                  className="btn btn-ghost btn-sm"
                  style={{ fontSize: 11, padding: '2px 9px', fontWeight: period === d ? 800 : 400,
                    background: period === d ? 'rgba(13,148,136,0.15)' : undefined,
                    border: period === d ? '1px solid rgba(13,148,136,0.4)' : undefined }}>
                  {d === 365 ? '1 yr' : `${d}d`}
                </button>
              ))}
              {dateRange?.earliest && <span style={{ fontSize: 10, opacity: 0.4, marginLeft: 6 }}>from {dateRange.earliest}</span>}
            </div>

            {/* Lunch / Dinner / All tabs */}
            <div style={{ display: 'flex', gap: 4 }}>
              {['Lunch', 'Dinner', 'All'].map(tab => {
                const col = tab === 'Lunch' ? PERIOD_COLOR_SM['Lunch'] : tab === 'Dinner' ? PERIOD_COLOR_SM['Dinner'] : '#0d9488';
                const active = serviceTab === tab;
                return (
                  <button key={tab} onClick={() => { setServiceTab(tab); setSortKey('rev_per_shift'); setSortDir('desc'); }}
                    style={{
                      fontSize: 12, padding: '4px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: active ? 700 : 400,
                      border: `1px solid ${active ? col : 'rgba(100,116,139,0.2)'}`,
                      background: active ? col + '22' : 'transparent', color: active ? col : '#64748b',
                    }}>
                    {tab}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Context note per tab */}
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 12, fontStyle: 'italic' }}>
            {serviceTab === 'Lunch'  && '☀️ Lunch — metrics calculated from lunch shifts only. Chks/shift reflects lunch table turnover.'}
            {serviceTab === 'Dinner' && '🌙 Dinner — metrics calculated from dinner shifts only. $/check should be meaningfully higher.'}
            {serviceTab === 'All'    && '📊 All services combined — use Lunch / Dinner tabs for fair comparisons.'}
          </div>

          {/* Insights chips */}
          {currentRows.length > 0 && <ServerInsights rows={currentRows} tab={serviceTab} />}

          {/* Table */}
          {currentRows.length > 0
            ? <ServiceTable rows={currentRows} isBanquet={false} sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
            : <div style={{ color: '#64748b', fontSize: 13, padding: '12px 0' }}>No {serviceTab} data in this period.</div>
          }

          {/* Bar section */}
          {currentBar.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                🍹 Bar
                <span style={{ fontSize: 10, fontWeight: 400, color: '#94a3b8', textTransform: 'none', letterSpacing: 0 }}>bartenders — higher chks/shift is expected, excluded from floor ranking</span>
              </div>
              <ServiceTable rows={currentBar} isBanquet={true} sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
            </div>
          )}

          {/* Banquet section */}
          {currentBanquet.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                🎪 Events & Banquet
                <span style={{ fontSize: 10, fontWeight: 400, color: '#94a3b8', textTransform: 'none', letterSpacing: 0 }}>excluded from main ranking</span>
              </div>
              <ServiceTable rows={currentBanquet} isBanquet={true} sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
            </div>
          )}

          <div style={{ marginTop: 8, fontSize: 10, color: '#94a3b8' }}>↑↓ click any column header to sort</div>
        </div>
      )}
    </div>
  );
}

// ── Sortable card header ─────────────────────────────────────────────────────
function SortHeader({ title, sort, onToggle }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
      <span style={{ flex: 1, minWidth: 0 }}>{title}</span>
      <button
        onClick={onToggle}
        title={sort === 'desc' ? 'Showing highest first — click for lowest' : 'Showing lowest first — click for highest'}
        style={{
          flexShrink: 0, fontSize: 11, padding: '2px 8px', cursor: 'pointer',
          background: 'rgba(100,116,139,0.1)', border: '1px solid rgba(100,116,139,0.2)',
          borderRadius: 4, whiteSpace: 'nowrap', lineHeight: 1.4,
        }}
      >
        {sort === 'desc' ? '↓ High' : '↑ Low'}
      </button>
    </div>
  );
}

// ── Tabs ─────────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'overview', label: '📊 Overview' },
  { key: 'servers',  label: '👥 Servers'  },
  { key: 'import',   label: '📤 Import'   },
];

function TabBar({ active, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid rgba(128,128,128,0.15)', paddingBottom: 0 }}>
      {TABS.map(t => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '8px 16px', fontSize: 13, fontWeight: active === t.key ? 700 : 500,
            color: active === t.key ? '#0d9488' : '#64748b',
            borderBottom: active === t.key ? '2px solid #0d9488' : '2px solid transparent',
            marginBottom: -1, transition: 'all .15s',
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ── Weather row with expandable DOW breakdown ─────────────────────────────────
function WeatherRow({ r, overall }) {
  const [open, setOpen] = useState(false);
  const hasDow = r.dow_breakdown?.length > 0;
  // Show up to 3 top days as a hint
  const topDays = (r.dow_breakdown || []).slice(0, 3).map(d => d.dow.slice(0,3)).join(', ');

  return (
    <div style={{ borderBottom: '1px solid rgba(255,255,255,.05)' }}>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', cursor: hasDow ? 'pointer' : 'default' }}
        onClick={() => hasDow && setOpen(o => !o)}
      >
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>
            {weatherEmoji(r.weather_summary)} {r.weather_summary}
            {hasDow && <span style={{ fontSize: 10, opacity: 0.45, marginLeft: 6 }}>{open ? '▲' : '▼'}</span>}
          </div>
          <div style={{ fontSize: 11, color: '#64748b' }}>
            {r.days} days
            {hasDow && !open && <span style={{ marginLeft: 6, opacity: 0.6 }}>mostly {topDays}</span>}
          </div>
        </div>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#0d9488' }}>{r.avg_covers} avg</div>
      </div>
      {open && hasDow && (
        <div style={{ paddingBottom: 10, paddingLeft: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>
            Day-of-week mix — was it busy regardless of weather?
          </div>
          {r.dow_breakdown.map(d => {
            const thinSample = d.days < 6;          // fewer than 6 occurrences = unreliable
            const dowAvgPct  = overall > 0 ? Math.round((d.avg_covers / overall) * 100) : null;
            const pctDiff    = dowAvgPct != null ? dowAvgPct - 100 : null;
            // Only colour rows with enough data
            const valueColor = thinSample ? '#64748b'
              : d.avg_covers > overall * 1.05 ? '#16a34a'
              : d.avg_covers < overall * 0.95 ? '#dc2626'
              : '#94a3b8';
            return (
              <div key={d.dow} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', opacity: thinSample ? 0.45 : 1 }}>
                <span style={{ color: '#94a3b8' }}>
                  {d.dow} <span style={{ opacity: 0.55 }}>({d.days}×)</span>
                  {thinSample && (
                    <span style={{ marginLeft: 5, fontSize: 10, color: '#94a3b8', fontStyle: 'italic' }}>too few to trust</span>
                  )}
                </span>
                <span style={{ color: valueColor, fontWeight: thinSample ? 400 : 600 }}>
                  {d.avg_covers} avg
                  {!thinSample && pctDiff != null && (
                    <span style={{ opacity: 0.6, fontWeight: 400 }}> ({pctDiff > 0 ? '+' : ''}{pctDiff}% overall)</span>
                  )}
                </span>
              </div>
            );
          })}
          <div style={{ marginTop: 6, fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>
            If the top days are naturally busy, that's driving the number — not the weather itself.
            Rows with fewer than 6 occurrences are dimmed (too small a sample to be reliable).
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main view ────────────────────────────────────────────────────────────────
export default function CoversImportView() {
  const [stats, setStats]       = useState(null);
  const [busy, setBusy]         = useState(false);
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState(null);
  const [weatherSort, setWeatherSort] = useState('desc');
  const [dowSort, setDowSort]   = useState('desc');
  const [tab, setTab]           = useState('overview');

  function loadStats() {
    api.get('/covers/stats').then(r => setStats(r.data)).catch(() => {});
  }
  useEffect(() => { loadStats(); }, []);

  async function handleFile(file) {
    setBusy(true);
    setError(null);
    setResult(null);
    const form = new FormData();
    form.append('file', file);
    try {
      const r = await api.post('/covers/import', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(r.data);
      loadStats();
    } catch (e) {
      setError(e.response?.data?.error || 'Import failed — check the file format');
    } finally {
      setBusy(false);
    }
  }

  const hasData = stats?.totals?.total_days > 0;

  return (
    <>
      <div className="page-header">
        <span className="page-title">Covers & Servers</span>
      </div>
      <div className="page-body">

        <TabBar active={tab} onChange={t => setTab(t)} />

        {/* ── OVERVIEW TAB ─────────────────────────────────────────────── */}
        {tab === 'overview' && (
          <>
            {!hasData && (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>No covers data yet</div>
                <div style={{ fontSize: 13 }}>Go to the <button onClick={() => setTab('import')} style={{ background: 'none', border: 'none', color: '#0d9488', cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}>Import tab</button> to upload your Toast CSV.</div>
              </div>
            )}

            {hasData && (
              <>
                {/* Stat cards */}
                <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                  <StatCard label="Days on record" value={stats.totals.total_days} sub={`${stats.totals.earliest?.slice(0,10)} → ${stats.totals.latest?.slice(0,10)}`} />
                  <StatCard label="Avg daily covers" value={stats.totals.avg_daily_covers} sub="across all days" />
                  <StatCard
                    label="Meal periods"
                    value={stats.totals.meal_periods}
                    sub={stats.totals.meal_periods < 2 ? 're-import to split by period' : 'in dataset'}
                    color="#818cf8"
                    tooltip={
                      <div>
                        <div style={{ fontWeight: 700, marginBottom: 6 }}>Derived from check open time:</div>
                        <div>🌅 <strong>Breakfast</strong> — before 11am</div>
                        <div>🥗 <strong>Lunch</strong> — 11am – 2:59pm</div>
                        <div>☕ <strong>Afternoon</strong> — 3pm – 4:59pm</div>
                        <div>🍽️ <strong>Dinner</strong> — 5pm – 9:59pm</div>
                        <div>🌙 <strong>Late Night</strong> — 10pm+</div>
                        <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid rgba(128,128,128,0.15)', opacity: 0.55, fontSize: 11 }}>Derived from the "Opened" time on each Toast check.</div>
                      </div>
                    }
                  />
                </div>

                {/* Insights */}
                <Insights stats={stats} />

                {/* AI chat */}
                <div style={{ marginBottom: 20 }}>
                  <CoversChat />
                </div>

                {/* Meal period chart */}
                {stats.byMealPeriod?.length > 0 && (
                  <MealPeriodChart data={stats.byMealPeriod} overall={stats.totals.avg_daily_covers} />
                )}

                {/* By weather + by DOW */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
                  <div className="card">
                    <div className="card-header">
                      <SortHeader title="Covers by Weather" sort={weatherSort} onToggle={() => setWeatherSort(s => s === 'desc' ? 'asc' : 'desc')} />
                    </div>
                    <div className="card-body" style={{ paddingTop: 8 }}>
                      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8, fontStyle: 'italic' }}>
                        Click a row to see which days of the week drove that weather average
                      </div>
                      {[...stats.byWeather]
                        .sort((a, b) => weatherSort === 'desc' ? b.avg_covers - a.avg_covers : a.avg_covers - b.avg_covers)
                        .map(r => (
                          <WeatherRow key={r.weather_summary} r={r} overall={stats.totals.avg_daily_covers} />
                        ))}
                    </div>
                  </div>

                  <div className="card">
                    <div className="card-header">
                      <SortHeader title="Covers by Day" sort={dowSort} onToggle={() => setDowSort(s => s === 'desc' ? 'asc' : 'desc')} />
                    </div>
                    <div className="card-body" style={{ paddingTop: 8 }}>
                      {[...stats.byDow]
                        .sort((a, b) => dowSort === 'desc' ? b.avg_covers - a.avg_covers : a.avg_covers - b.avg_covers)
                        .map(r => (
                          <InsightRow key={r.day_of_week}
                            label={r.day_of_week}
                            sub={`${r.days} days`}
                            value={`${r.avg_covers} avg`} />
                        ))}
                      <DowLineChart byDowMealPeriod={stats.byDowMealPeriod || []} />
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* ── SERVERS TAB ──────────────────────────────────────────────── */}
        {tab === 'servers' && (
          <>
            {!hasData ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>👥</div>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>No server data yet</div>
                <div style={{ fontSize: 13 }}>Import a Toast Orders CSV that includes a <strong>Server</strong> column to see leaderboard data.</div>
              </div>
            ) : (
              <ServerLeaderboard expanded />
            )}
          </>
        )}

        {/* ── IMPORT TAB ───────────────────────────────────────────────── */}
        {tab === 'import' && (
          <div className="card">
            <div className="card-body">
              {/* How-to */}
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid rgba(128,128,128,0.12)' }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                  background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                }}>🍞</div>
                <div style={{ fontSize: 13, lineHeight: 1.65, opacity: 0.75 }}>
                  <strong style={{ opacity: 1 }}>Toast → Reports → Orders/Checks</strong>.
                  Include: <strong style={{ opacity: 1 }}>Opened, # of Guests, Amount, Server</strong>.
                  Export CSV — up to 3 months at a time. Re-upload older ranges freely; no duplicates created.
                </div>
              </div>

              <DropZone onFile={handleFile} busy={busy} />

              {busy && (
                <div style={{ textAlign: 'center', marginTop: 16, color: '#0d9488', fontSize: 13 }}>
                  Parsing CSV and fetching historical weather… this takes a few seconds
                </div>
              )}
              {error && (
                <div style={{ marginTop: 12, padding: '12px 16px', background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.25)', borderRadius: 8, fontSize: 13, color: '#ef4444' }}>
                  ⚠️ {error}
                </div>
              )}
              {result && (
                <div style={{ marginTop: 12, padding: '14px 16px', background: 'rgba(13,148,136,.08)', border: '1px solid rgba(13,148,136,.25)', borderRadius: 8 }}>
                  <div style={{ fontWeight: 700, color: '#0d9488', marginBottom: 4 }}>✓ Import complete</div>
                  <div style={{ fontSize: 13, opacity: 0.8 }}>
                    <strong>{result.imported}</strong> days imported
                    {result.skipped > 0 && <span> · {result.skipped} skipped</span>}
                    {result.servers > 0 && <span> · <strong>{result.servers}</strong> server records</span>}
                    {' · '}{result.dateRange?.start} → {result.dateRange?.end}
                    {result.location && <span> · {result.location.lat.toFixed(2)}°, {result.location.lon.toFixed(2)}°</span>}
                  </div>
                  {result.servers > 0 && (
                    <button onClick={() => setTab('servers')} style={{ marginTop: 8, fontSize: 12, color: '#0d9488', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                      View server leaderboard →
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </>
  );
}
